/**
 * Local column manager for modal grids (Direct Delivery, Warehouse Delivery).
 * Same UI as the stock planning ColumnManager but stores state locally
 * rather than persisting to the backend.
 */
import { Settings2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import type { LocalColumn } from './DirectDeliveryColumns';

interface LocalColumnManagerProps {
  columns: LocalColumn[];
  onToggle: (columnId: string) => void;
  onMoveUp: (columnId: string) => void;
}

export default function LocalColumnManager({ columns, onToggle, onMoveUp }: LocalColumnManagerProps) {
  const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 className="h-4 w-4" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-96 overflow-y-auto p-2">
        <div className="space-y-1">
          {sorted.map((col, index) => (
            <div
              key={col.id}
              className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <button
                className="flex items-center gap-2 flex-1 text-left text-sm"
                onClick={() => onToggle(col.id)}
              >
                {col.visible ? (
                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
                )}
                <span className={col.visible ? '' : 'text-muted-foreground opacity-50'}>
                  {col.label}
                </span>
              </button>
              <div className="flex gap-0.5">
                <button
                  className="p-0.5 hover:bg-background rounded"
                  onClick={() => onMoveUp(col.id)}
                  disabled={index === 0}
                >
                  <GripVertical className="h-3 w-3 rotate-90 text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
